import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchDigests, fetchInsight } from '../lib/api.js';
import type { DigestRecord, Insight } from '@claude-code-insights/shared';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export default function DigestsView() {
  const [digests, setDigests] = useState<DigestRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentYear, setCurrentYear] = useState(() => new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(() => new Date().getMonth());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedInsights, setSelectedInsights] = useState<Insight[]>([]);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [filterText, setFilterText] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchDigests().then(setDigests).finally(() => setLoading(false));
  }, []);

  const digestsByDate = useMemo(
    () => new Map(digests.map((d) => [d.date, d])),
    [digests],
  );

  useEffect(() => {
    setFilterText('');
    if (!selectedDate) return;
    const digest = digestsByDate.get(selectedDate);
    if (!digest) return;

    const allIds = [...digest.insightIds];
    if (digest.tipInsightId && !allIds.includes(digest.tipInsightId)) {
      allIds.push(digest.tipInsightId);
    }

    setLoadingInsights(true);
    setSelectedInsights([]);
    Promise.all(allIds.map((id) => fetchInsight(id)))
      .then((results) => setSelectedInsights(results.map((r) => r.insight)))
      .catch(() => setSelectedInsights([]))
      .finally(() => setLoadingInsights(false));
  }, [selectedDate, digestsByDate]);

  function prevMonth() {
    if (currentMonth === 0) { setCurrentYear((y) => y - 1); setCurrentMonth(11); }
    else setCurrentMonth((m) => m - 1);
  }

  function nextMonth() {
    if (currentMonth === 11) { setCurrentYear((y) => y + 1); setCurrentMonth(0); }
    else setCurrentMonth((m) => m + 1);
  }

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstWeekday = new Date(currentYear, currentMonth, 1).getDay();
  const today = new Date().toISOString().slice(0, 10);

  const visibleInsights = filterText.trim()
    ? selectedInsights.filter(
        (i) =>
          i.title.toLowerCase().includes(filterText.toLowerCase()) ||
          i.summary.toLowerCase().includes(filterText.toLowerCase()),
      )
    : selectedInsights;

  return (
    <div className="flex flex-col md:flex-row h-full max-w-5xl mx-auto p-6 gap-6">
      {/* Left: Calendar */}
      <div className="md:w-72 flex-shrink-0">
        <div className="bg-obsidian-bg-secondary border border-obsidian-border rounded-lg p-4">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-4">
            <button onClick={prevMonth} className="text-obsidian-text-muted hover:text-obsidian-text p-1 transition-colors">◀</button>
            <span className="text-sm font-semibold text-obsidian-text">{MONTH_NAMES[currentMonth]} {currentYear}</span>
            <button onClick={nextMonth} className="text-obsidian-text-muted hover:text-obsidian-text p-1 transition-colors">▶</button>
          </div>

          {/* Weekday headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
              <div key={i} className="text-center text-xs text-obsidian-text-faint py-1">{d}</div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: firstWeekday }).map((_, i) => <div key={`empty-${i}`} />)}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
              const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const hasDigest = digestsByDate.has(dateStr);
              const isSelected = selectedDate === dateStr;
              const isToday = dateStr === today;
              return (
                <button
                  key={day}
                  onClick={() => hasDigest && setSelectedDate(dateStr)}
                  className={`relative text-center text-xs rounded p-1.5 transition-colors ${
                    isSelected
                      ? 'bg-obsidian-accent text-white font-semibold'
                      : hasDigest
                        ? 'bg-obsidian-surface text-obsidian-text hover:bg-obsidian-bg-tertiary cursor-pointer'
                        : 'text-obsidian-text-faint cursor-default'
                  } ${isToday && !isSelected ? 'ring-1 ring-obsidian-accent' : ''}`}
                >
                  {day}
                  {hasDigest && !isSelected && (
                    <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-obsidian-accent" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="mt-4 pt-3 border-t border-obsidian-border">
            <div className="flex items-center gap-2 text-xs text-obsidian-text-faint">
              <span className="inline-block w-2 h-2 rounded-full bg-obsidian-accent flex-shrink-0" />
              Has digest
            </div>
          </div>
        </div>

        {loading && <p className="text-xs text-obsidian-text-muted mt-3 text-center">Loading digests...</p>}
        {!loading && digests.length === 0 && <p className="text-xs text-obsidian-text-muted mt-3 text-center">No digests yet.</p>}
      </div>

      {/* Right: Day detail */}
      <div className="flex-1 min-w-0">
        {!selectedDate && (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <div className="text-3xl mb-3">📅</div>
            <p className="text-sm text-obsidian-text-muted">Select a highlighted date to see that day's digest</p>
          </div>
        )}

        {selectedDate && (
          <div>
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-3">
                <h2 className="text-base font-semibold text-obsidian-text">
                  {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
                    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
                  })}
                </h2>
                <span className="text-xs text-obsidian-text-muted">
                  · {digestsByDate.get(selectedDate)?.insightIds.length ?? 0} insights
                </span>
              </div>
              <input
                type="text"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                placeholder="Filter insights..."
                className="w-full bg-obsidian-bg-secondary border border-obsidian-border rounded-lg px-3 py-1.5 text-sm text-obsidian-text placeholder:text-obsidian-text-faint focus:outline-none focus:border-obsidian-accent transition-colors"
              />
            </div>

            {loadingInsights && <p className="text-sm text-obsidian-text-muted">Loading insights...</p>}

            <div className="space-y-3">
              {visibleInsights.map((insight) => (
                <div
                  key={insight.insightId}
                  className="bg-obsidian-bg-secondary border border-obsidian-border rounded-lg p-4 hover:border-obsidian-border-light cursor-pointer transition-colors"
                  onClick={() => navigate(`/insight/${insight.insightId}`)}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="text-sm font-semibold text-obsidian-text">{insight.title}</h3>
                    <span className="text-xs text-white bg-obsidian-accent rounded-full px-2 py-0.5 flex-shrink-0">
                      {insight.relevanceScore}/10
                    </span>
                  </div>
                  <p className="text-xs text-obsidian-text-muted line-clamp-2 mb-2">{insight.summary}</p>
                  <div className="flex gap-1 flex-wrap">
                    <span className="text-xs bg-obsidian-surface text-obsidian-text-muted px-2 py-0.5 rounded">{insight.category}</span>
                    {insight.tags.slice(0, 3).map((tag) => (
                      <span key={tag} className="text-xs bg-obsidian-surface text-obsidian-text-muted px-2 py-0.5 rounded">{tag}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
